import React, { useState } from 'react';
import { ArrowRight, CheckIcon, ChevronDown } from './icons';
import { countWords, isAnswered } from './progress';

const SaveStatus = ({ saveFailed, children }) => (
  <div className={`panel-footer${saveFailed ? ' is-error' : ''}`} role={saveFailed ? 'alert' : undefined}>
    {saveFailed ? (
      'Klarte ikke å lagre. Arbeidet blir borte hvis du lukker siden – lag PDF nå.'
    ) : (
      <>
        <CheckIcon size={16} />
        {children}
      </>
    )}
  </div>
);

const CoverPanel = ({ book, name, onNameChange, onStart, saveFailed }) => (
  <section className="panel" aria-label="Om boka">
    <div className="panel-body cover-body">
      <h1 className="cover-title">{book.title}</h1>
      <p className="lead">{book.cover.intro}</p>
      <ol className="steps">
        {book.cover.steps.map((step, i) => (
          <li key={i}><span className="step-number">{i + 1}</span>{step}</li>
        ))}
      </ol>
      <div className="field">
        <label htmlFor="student-name">Navnet ditt</label>
        <input id="student-name" type="text" value={name} onChange={(e) => onNameChange(e.target.value)}
          placeholder="Fornavn og etternavn" autoComplete="name" />
        <div className="hint">Navnet kommer på forsiden av PDF-en.</div>
      </div>
      <div className="spacer" />
      <button className="primary-button start-button" onClick={onStart}>
        Start på side 1 <ArrowRight size={20} />
      </button>
    </div>
    <SaveStatus saveFailed={saveFailed}>Arbeidet lagres automatisk på denne maskinen.</SaveStatus>
  </section>
);

const QuestionsPanel = ({ page, pageIndex, answers, onAnswerChange, saveFailed }) => {
  const done = page.questions.filter((_, i) => isAnswered(answers[i])).length;
  return (
    <section className="panel" aria-label="Tekst og spørsmål">
      <div className="panel-body">
        {page.label && <div><span className="chip">{page.label}</span></div>}
        <h1 className="page-title">{page.title}</h1>
        <p className="page-text">{page.text}</p>
        <h2 className="section-heading">Svar på spørsmålene</h2>
        {page.questions.map((question, i) => (
          <div className="question" key={i}>
            <label htmlFor={`answer-${pageIndex}-${i}`}>
              <span className="question-number">{i + 1}</span>
              {question}
            </label>
            <textarea id={`answer-${pageIndex}-${i}`} rows={4} placeholder="Skriv svaret ditt her …"
              value={answers[i] || ''} onChange={(e) => onAnswerChange(i, e.target.value)} />
          </div>
        ))}
      </div>
      <SaveStatus saveFailed={saveFailed}>
        Lagret på denne maskinen · {done} av {page.questions.length} svar skrevet
      </SaveStatus>
    </section>
  );
};

const ReferatPanel = ({ book, page, pageIndex, answers, onAnswerChange, saveFailed }) => {
  const [showHelp, setShowHelp] = useState(false);
  const text = answers[0] || '';
  const helpId = `help-${pageIndex}`;
  return (
    <section className="panel" aria-label="Referat">
      <div className="panel-body">
        <div><span className="chip">Referat</span></div>
        <h1 className="page-title">Hva skjer på dette bildet?</h1>
        <p className="instruction">Skriv hva som skjer i denne delen av <em>{book.title}</em>, med egne ord. Bruk bildet og ordene under til hjelp.</p>
        <div className="word-bank">
          <h2 className="section-heading">Ord du kan bruke</h2>
          <ul>
            {page.words.map((word) => <li key={word}>{word}</li>)}
          </ul>
        </div>
        <div className="question">
          <div className="label-row">
            <label htmlFor={`referat-${pageIndex}`}>Ditt referat</label>
            <span className="word-count">{countWords(text)} ord</span>
          </div>
          <textarea id={`referat-${pageIndex}`} className="referat-text" rows={8} placeholder="Skriv hva som skjer …"
            value={text} onChange={(e) => onAnswerChange(0, e.target.value)} />
        </div>
        <div className="help-box">
          <button className="help-toggle" aria-expanded={showHelp} aria-controls={helpId} onClick={() => setShowHelp(!showHelp)}>
            Står du fast? Vis hva som skjer
            <ChevronDown size={18} className={showHelp ? 'is-open' : ''} />
          </button>
          {showHelp && <p id={helpId} className="help-text">{page.text}</p>}
        </div>
      </div>
      <SaveStatus saveFailed={saveFailed}>Lagret på denne maskinen · Referatet kommer med i PDF-en</SaveStatus>
    </section>
  );
};

const Panel = ({ book, pageIndex, answers, onAnswerChange, name, onNameChange, onStart, saveFailed }) => {
  if (pageIndex === 0) {
    return <CoverPanel book={book} name={name} onNameChange={onNameChange} onStart={onStart} saveFailed={saveFailed} />;
  }
  const page = book.pages[pageIndex - 1];
  const props = { book, page, pageIndex, answers, onAnswerChange, saveFailed };
  return page.mode === 'referat'
    ? <ReferatPanel key={pageIndex} {...props} />
    : <QuestionsPanel {...props} />;
};

export default Panel;
